import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormShell,
} from '@shigo-ui/dashboard-ui';
import {
  AutocompleteMultiSelect,
  AutocompleteSingleSelect,
  Button,
  Form,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  toast,
} from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import {
  supplyInventoryAppFormSchema,
  supplyInventoryAppToFormValue,
  serializeSupplyInventoryAppUpdate,
  type SupplyInventoryAppFormValue,
} from '../../appFormSchema';
import { useSupplyAppQuery, useSupplyAppsMutations, useSupplyAppsReferences } from '../../hooks';

export function AppEditorPage({ appId }: { appId: string }) {
  const { returnTo } = useReturnNavigation({ to: '/supply/apps' });
  const query = useSupplyAppQuery(appId);
  const references = useSupplyAppsReferences();
  const mutation = useSupplyAppsMutations().update;
  const goBack = () => void returnTo();

  async function save(values: SupplyInventoryAppFormValue) {
    if (!query.data) return;
    try {
      await mutation.mutateAsync({
        id: appId,
        payload: serializeSupplyInventoryAppUpdate(query.data, values),
      });
      toast.success(dashboardT('supplyApps.updatedSuccess'));
    } catch (error) {
      showRequestError(error, dashboardT('supplyApps.saveError'));
      return;
    }
    try {
      await returnTo({ replace: true });
    } catch {
      toast.error(dashboardT('supplyApps.redirectError'));
    }
  }

  let content = null;
  if (!query.isFetchedAfterMount && !query.isError) {
    content = (
      <div
        role="status"
        aria-label={dashboardT('supplyApps.loadingDetails')}
        className="grid gap-4 p-6"
      >
        <Skeleton className="h-10 w-72 max-w-full" />
        <Skeleton className="h-96" />
      </div>
    );
  } else if (query.isError) {
    content = (
      <DashboardPageFormBody title={dashboardT('supplyApps.loadDetailsError')}>
        <Button variant="outline" onClick={() => void query.refetch()}>
          {dashboardT('supplyApps.retry')}
        </Button>
      </DashboardPageFormBody>
    );
  } else if (query.data) {
    content = (
      <AppEditorForm
        key={query.data.id}
        initialValue={supplyInventoryAppToFormValue(query.data)}
        saving={mutation.isPending}
        references={references}
        onCancel={goBack}
        onSubmit={save}
      />
    );
  }

  return (
    <DashboardPageFormShell
      title={dashboardT('supplyApps.editTitle')}
      subtitle={dashboardT('supplyApps.formDescription')}
      backAction={
        <ReturnBackLink fallback={{ to: '/supply/apps' }} label={dashboardT('supplyApps.back')} />
      }
    >
      {content}
    </DashboardPageFormShell>
  );
}

function AppEditorForm({
  initialValue,
  saving,
  references,
  onCancel,
  onSubmit,
}: {
  initialValue: SupplyInventoryAppFormValue;
  saving: boolean;
  references: ReturnType<typeof useSupplyAppsReferences>;
  onCancel: () => void;
  onSubmit: (value: SupplyInventoryAppFormValue) => Promise<void>;
}) {
  'use no memo';
  const form = useForm<SupplyInventoryAppFormValue>({
    defaultValues: initialValue,
    resolver: zodResolver(supplyInventoryAppFormSchema),
  });
  const errors = form.formState.errors;
  return (
    <Form {...form}>
      <form noValidate onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}>
        <DashboardPageFormBody width="lg">
          <div className="grid gap-4 md:grid-cols-2">
            <DashboardPageFormField
              label={dashboardT('supplyApps.name')}
              htmlFor="supply-app-name"
              required
              error={errors.name?.message}
            >
              <Input
                id="supply-app-name"
                required
                aria-invalid={Boolean(errors.name)}
                {...form.register('name')}
              />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('supplyApps.language')}
              htmlFor="supply-app-language"
            >
              <Input id="supply-app-language" {...form.register('language')} />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('supplyApps.bundleId')}
              htmlFor="supply-app-bundle"
              required
              error={errors.bundleId?.message}
            >
              <Input
                id="supply-app-bundle"
                required
                aria-invalid={Boolean(errors.bundleId)}
                {...form.register('bundleId')}
              />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('supplyApps.appStoreId')}
              htmlFor="supply-app-store-id"
              required
              error={errors.appStoreId?.message}
            >
              <Input
                id="supply-app-store-id"
                required
                readOnly
                aria-invalid={Boolean(errors.appStoreId)}
                {...form.register('appStoreId')}
              />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('supplyApps.appStoreUrl')}
              htmlFor="supply-app-store-url"
              required
              error={errors.appStoreUrl?.message}
            >
              <Input
                id="supply-app-store-url"
                required
                aria-invalid={Boolean(errors.appStoreUrl)}
                {...form.register('appStoreUrl')}
              />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('supplyApps.domain')}
              htmlFor="supply-app-domain"
            >
              <Input id="supply-app-domain" {...form.register('domain')} />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('supplyApps.platform')}
              required
              error={errors.platformId?.message}
            >
              <Controller
                control={form.control}
                name="platformId"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange} disabled>
                    <SelectTrigger className="h-9" aria-label={dashboardT('supplyApps.platform')}>
                      <SelectValue placeholder={dashboardT('supplyApps.selectPlatform')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(references.platforms.data ?? []).map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('supplyApps.numericId')}
              htmlFor="supply-app-numeric-id"
            >
              <Input id="supply-app-numeric-id" readOnly {...form.register('numericId')} />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('supplyApps.adsTxtLocation')}
              htmlFor="supply-app-adstxt"
            >
              <Input id="supply-app-adstxt" {...form.register('adsTxtLocation')} />
            </DashboardPageFormField>
            <DashboardPageFormField label={dashboardT('supplyApps.publisher')}>
              <Controller
                control={form.control}
                name="publisherIds"
                render={({ field }) => (
                  <AutocompleteMultiSelect
                    label={dashboardT('supplyApps.publisher')}
                    placeholder={dashboardT('supplyApps.selectPublishers')}
                    options={(references.publishers.data ?? []).map((item) => ({
                      id: item.id,
                      label: item.name,
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                    loading={references.publishers.isLoading}
                  />
                )}
              />
            </DashboardPageFormField>
            <DashboardPageFormField label={dashboardT('supplyApps.iabCategories')}>
              <Controller
                control={form.control}
                name="iabCategoryIds"
                render={({ field }) => (
                  <AutocompleteSingleSelect
                    label={dashboardT('supplyApps.iabCategories')}
                    placeholder={dashboardT('supplyApps.selectIabCategories')}
                    options={(references.iabCategories.data ?? []).map((item) => ({
                      id: item.id,
                      label: item.name,
                    }))}
                    value={field.value[0] ?? ''}
                    onValueChange={(value) => {
                      field.onChange(value ? [value] : []);
                    }}
                    loading={references.iabCategories.isLoading}
                  />
                )}
              />
            </DashboardPageFormField>
            <div className="md:col-span-2">
              <DashboardPageFormField
                label={dashboardT('supplyApps.description')}
                htmlFor="supply-app-description"
              >
                <Textarea id="supply-app-description" rows={4} {...form.register('description')} />
              </DashboardPageFormField>
            </div>
          </div>
        </DashboardPageFormBody>
        <DashboardPageFormActions>
          <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>
            {dashboardT('supplyApps.cancel')}
          </Button>
          <Button type="submit" disabled={saving}>
            {dashboardT(saving ? 'supplyApps.saving' : 'supplyApps.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}
